import { makeStyles } from "tss-react/mui";
import { DRAWER_WIDTH_FULL, DRAWER_WIDTH_LG } from "@miplatform/constants";
import { mdiClose } from "@mdi/js";
import { Box, Drawer, useMediaQuery, useTheme } from "@mui/material";
import { MiActions } from "../MiActions/MiActions";
import { MiLoader } from "../MiLoader/MiLoader";
import { MiSectionHeader } from "../MiSectionHeader/MiSectionHeader";

/**
 * Renders a customizable drawer with a header, content area, and action buttons.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The content to be displayed inside the drawer.
 * @param {number} [props.width] - The width of the drawer.
 * @param {string} [props.leftIcon] - The icon to be displayed on the left side of the header.
 * @param {string} [props.leftIconColor] - The color of the left icon.
 * @param {string} [props.rightIcon] - The icon to be displayed on the right side of the header.
 * @param {string} [props.rightIconColor] - The color of the right icon.
 * @param {string} [props.color] - The color of the header.
 * @param {boolean} [props.hideBackdrop] - Whether to hide the backdrop when the drawer is open.
 * @param {string} [props.variant="persistent"] - The variant of the drawer, either "persistent" or "temporary".
 * @param {string} [props.titleVariant="h6"] - The variant of the title text.
 * @param {boolean} [props.open=false] - Whether the drawer is open.
 * @param {string} [props.title=""] - The title text to be displayed in the header.
 * @param {string} [props.subtitle] - The subtitle text to be displayed in the header.
 * @param {Array<Object>} [props.actions=[]] - The actions to be displayed in the action container.
 * @param {function} [props.onClose=() => null] - The function to be called when the drawer is closed.
 * @param {boolean} [props.isLoading=false] - Whether to show a loading indicator.
 * @param {React.ReactNode} [props.Header=null] - Custom header component to replace the default header.
 * @param {Object} [props.sx] - Custom styles to be applied to the drawer.
 * @param {Object} [props.rest] - Additional properties to be passed to the Drawer component.
 *
 * @returns {JSX.Element} The rendered MiContentDrawer component.
 */
export function MiContentDrawer({
  children,
  width,
  leftIcon,
  leftIconColor,
  color,
  hideBackdrop,
  variant = "persistent",
  titleVariant = "h6",
  open = false,
  title = "",
  actions = [],
  onClose = () => null,
  subtitle,
  isLoading = false,
  Header = null,
  removeMargin = false,
  sx,
  ...rest
}) {
  const { classes } = useStyles();
  const theme = useTheme();
  const isLg = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const drawerWidth = width || (isLg ? DRAWER_WIDTH_LG : DRAWER_WIDTH_FULL);
  const backdropHidden = hideBackdrop ?? variant === "persistent";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant={variant}
      hideBackdrop={backdropHidden}
      transitionDuration={250}
      sx={{ zIndex: theme.zIndex.modal, ...sx }}
      ModalProps={{
        keepMounted: false,
      }}
      {...rest}
    >
      <Box className={classes.drawerContainer} sx={{ width: drawerWidth }}>
        {Header}
        {title && (
          <Box className={classes.headerContainer}>
            <MiSectionHeader
              title={title}
              titleVariant={titleVariant}
              subtitle={subtitle}
              leftIcon={leftIcon}
              leftIconColor={leftIconColor}
              color={color}
              rightIcon={mdiClose}
              rightIconColor={theme.palette.grey.darkAccentDark}
              onRightIconClick={onClose}
              removeMargin={removeMargin}
            />

            <MiLoader
              isLoading={isLoading}
              type="linear"
              style={{ position: "absolute", top: 0 }}
            />
          </Box>
        )}
        <Box className={classes.contentContainer}>{children}</Box>
        {actions.length > 0 && (
          <Box className={classes.actionContainer}>
            <MiActions actions={actions} />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

const useStyles = makeStyles()((theme) => ({
  drawerContainer: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    transition: "width 0.25s ease-in-out",
  },
  headerContainer: {
    position: "sticky",
    top: 0,
  },
  contentContainer: { display: "flex", flex: 1, overflowY: "auto" },
  actionContainer: {
    position: "sticky",
    bottom: "0",
    minHeight: 57,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "4px 16px",
    gap: 16,
    borderTop: `1px solid ${theme.palette.grey.accent}`,
    boxShadow: "0px 2px 12px 0px rgba(0, 0, 0, 0.1)",
  },
}));
